import axios from 'axios';
import { getStoredToken, getStoredSsoToken, clearAuthData } from '@/utils/auth';

const API_BASE_URL =
  import.meta.env.VITE_PREPLAY_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Environment verification logging
console.log('[AUTH] [API] Axios instance created:', {
  baseURL: API_BASE_URL,
  environment: import.meta.env.MODE,
  isLocal: API_BASE_URL.includes('localhost'),
  isAzure: API_BASE_URL.includes('azurewebsites.net'),
  envVars: {
    VITE_PREPLAY_API_URL: import.meta.env.VITE_PREPLAY_API_URL || 'not_set',
    VITE_API_URL: import.meta.env.VITE_API_URL || 'not_set',
  },
});

if (import.meta.env.MODE === 'development' && API_BASE_URL.includes('azurewebsites.net')) {
  console.warn('[AUTH] [API] WARNING: Development mode but pointing to Azure backend!');
  console.warn('[AUTH] [API] Expected: http://localhost:3001');
  console.warn(
    '[AUTH] [API] Create .env.development.local with VITE_API_URL=http://localhost:3001'
  );
}

api.interceptors.request.use(
  async config => {
    const ssoToken = getStoredSsoToken();
    const legacyToken = getStoredToken();

    const token = ssoToken || legacyToken;

    // Comprehensive request logging
    console.log('[API] 📤 Outgoing Request:', {
      timestamp: new Date().toISOString(),
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      hasToken: !!token,
      tokenType: token ? (ssoToken ? 'SSO' : 'Legacy') : 'None',
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'N/A',
      headers: {
        ...config.headers,
        Authorization: token ? '***REDACTED***' : undefined,
      },
      // Log payload for POST/PUT requests (especially /login)
      data: config.data
        ? config.url?.includes('/login')
          ? {
              ...config.data,
              token: config.data.token ? `${config.data.token.substring(0, 20)}...` : undefined,
            }
          : config.data
        : undefined,
      params: config.params,
    });

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    console.error('[API] ❌ Request Error:', {
      timestamp: new Date().toISOString(),
      error: error.message,
      stack: error.stack,
    });
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => {
    // Comprehensive response logging
    console.log('[API] ✅ Response Received:', {
      timestamp: new Date().toISOString(),
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
      // Log response data (redact sensitive fields)
      data: response.config.url?.includes('/login')
        ? {
            ...response.data,
            token: response.data?.token ? `${response.data.token.substring(0, 20)}...` : undefined,
          }
        : response.data,
      headers: response.headers,
    });
    return response;
  },
  error => {
    // Comprehensive error logging
    console.error('[API] ❌ Response Error:', {
      timestamp: new Date().toISOString(),
      message: error.message,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      // Log full error response from backend (includes AMELCO errors, external IP, etc.)
      responseData: error.response?.data,
      responseHeaders: error.response?.headers,
      requestData: error.config?.data,
      // Network error details
      isNetworkError: !error.response,
      isTimeout: error.code === 'ECONNABORTED',
      errorCode: error.code,
      stack: error.stack,
    });

    if (error.response?.status === 401) {
      console.warn('[API] 🔒 401 Unauthorized - Clearing auth data');
      clearAuthData();
      // Note: No redirect - parent app should handle authentication
    }

    return Promise.reject(error);
  }
);
